import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type JSX } from "react";
import {
  SMART_LIST_VISIBILITIES,
  type CreateSmartListBody,
  type FilterDefinition,
  type LeadListQuery,
  type SmartListVisibility,
  type TeamMember,
  type TeamRow,
} from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../api/http";
import { teamQuery } from "../api/queries";
import { Button } from "../design/components/Button";
import { Checkbox } from "../design/components/Checkbox";
import { Dialog } from "../design/components/Dialog";
import { Reason } from "../design/components/Reason";
import { TextField } from "../design/components/TextField";
import "../design/components/select.css";
import { messageForError } from "../i18n/errors";
import { formatCount } from "../i18n/format";
import { useLocale, useT, type Translate } from "../i18n/locale";
import type { LeadSort } from "../leads/columns/table-layout";
import { leadListQuery, leadVisibilityQuery } from "../leads/lead-queries";
import { commercialViewOf, type TabContext } from "../session/tab-context";
import { useCreateSmartList } from "./smart-list-queries";
import "./smart-lists.css";

// «Crea Smart List» (piano M3 Task 8, D7 e D14; mock-up `lead.html` e `setupNewListDialog`): il nome, «Chi la vede»
// («Privata», «Condivisa con colleghi scelti», «Pubblica al team», con la spiegazione sotto ogni scelta), i colleghi
// soltanto per la lista condivisa, e «Quali Lead entrano nella lista»: i Lead di questi filtri con il numero di oggi e,
// per chi modifica tutte le liste, «I Lead di un Commerciale, per lui», che mette il filtro dell'owner su di lui e ne fa
// l'owner della lista (product owner, 28 settembre 2026). Si salvano filtri e ordinamento: mai la ricerca libera né «Di
// chi» (decisione P5), che la finestra dice in una riga quando «I miei» è attivo. Il numero di oggi è il totale di
// `GET /api/leads` con `who=all`, lo stesso che la lista conterà. «A mano» non c'è: arriva con le liste manuali
// (questione 140). Il Super admin di Gioia365 non ha un'appartenenza: crea soltanto liste per un Commerciale, e senza
// un Commerciale da scegliere (team che si legge, che non risponde, senza User attivi) «Crea e apri» resta spento con
// il motivo (decisione P29); «I Lead di questi filtri», che farebbe una lista sua, è spenta con il motivo (decisione P40),
// anche in «Salva come nuova». Sotto le scelte una riga dice che nessun Lead cambia owner: la lista sceglie soltanto
// quali Lead mostrare (riscontro del product owner del 2 ottobre 2026). La finestra nasce a ogni apertura e non ricorda
// la volta prima.

type Props = {
  tab: TabContext;
  definition: FilterDefinition;
  sort: LeadSort;
  /** Vuoto dall'elenco Lead; «{nome} (copia)» da «Salva come nuova». */
  initialName?: string;
  /**
   * «create» dall'elenco Lead («Crea Smart List», «Crea e apri»); «saveAs» dalla pagina di una lista, con le parole del
   * mock-up: «Salva come nuova», «Nome della nuova lista», «La lista originale non cambia…», «Salva e apri».
   */
  variant?: "create" | "saveAs";
  /** La modifica di tutte le liste (`smart_lists.update: all`): «I Lead di un Commerciale, per lui». */
  forOthers: boolean;
  /** «Di chi» = «I miei» sull'elenco: non si salva, e la finestra lo dice. */
  mineActive: boolean;
  onCreated(id: string): void;
  onClose(): void;
};

/** Il massimo del nome (`createSmartListBodySchema`): oltre, il server risponderebbe 422. */
const NAME_MAX_LENGTH = 120;
/** Il livello dei Commerciali (User): i soli con cui si condivide una lista o per cui la si crea, come nel mock-up. */
const COMMERCIAL_LEVEL = 1;

type Field = "name" | "recipients" | "person" | "form";
type Problem = { field: Field; text: (t: Translate) => string };
type Scope = "filters" | "person";

/** Gli User attivi dell'azienda, in ordine di nome: chi può ricevere una lista condivisa o averne una per sé. */
function commercials(team: readonly TeamRow[], locale: string): TeamMember[] {
  const collator = new Intl.Collator(locale, { sensitivity: "base" });
  return team
    .filter((row): row is TeamMember => row.kind === "membership" && row.status === "active" && row.role.level === COMMERCIAL_LEVEL)
    .sort((left, right) => collator.compare(left.displayName, right.displayName) || (left.membershipId < right.membershipId ? -1 : 1));
}

/** La definizione «per lui»: il filtro dell'owner, se c'era, lascia il posto a quello sul Commerciale, in fondo. */
function forPersonDefinition(definition: FilterDefinition, membershipId: string): FilterDefinition {
  return {
    ...definition,
    conditions: [...definition.conditions.filter((condition) => condition.field !== "owner"), { kind: "choice", field: "owner", op: "in", values: [membershipId] }],
  };
}

/** Il totale da solo di una definizione, con `who=all`: la stessa lettura dell'elenco, una riga. */
function countQueryOf(definition: FilterDefinition, sort: LeadSort): LeadListQuery {
  return { filter: definition, who: "all", sort: sort.field, direction: sort.direction, offset: 0, limit: 1 };
}

/** I campi di cui il server dice che non vanno (`422 validation_failed`, `details.fields`). */
function invalidFields(error: ApiRequestError): string[] {
  const fields = error.details?.fields;
  return Array.isArray(fields) ? fields.filter((field): field is string => typeof field === "string") : [];
}

export function NewSmartListDialog({ tab, definition, sort, initialName = "", variant = "create", forOthers, mineActive, onCreated, onClose }: Props): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const baseId = useId();
  const formId = `${baseId}-form`;
  const create = useCreateSmartList(tab);
  const view = commercialViewOf(tab);
  const me = tab.kind === "membership" ? tab.membershipId : null;

  const [name, setName] = useState(initialName);
  const [visibility, setVisibility] = useState<SmartListVisibility>("private");
  const [recipients, setRecipients] = useState<ReadonlySet<string>>(new Set());
  // Il Super admin di Gioia365 non ha un'appartenenza: la lista è sempre di un Commerciale.
  const [scope, setScope] = useState<Scope>(forOthers && me === null ? "person" : "filters");
  const [chosenPerson, setChosenPerson] = useState("");
  const [problem, setProblem] = useState<Problem | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const personRef = useRef<HTMLSelectElement>(null);
  const shareRef = useRef<HTMLFieldSetElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  // Il Super admin di Gioia365: la lista è sempre di un Commerciale.
  const ownerRequired = me === null;
  const team = useQuery({ ...teamQuery(tab), enabled: forOthers || ownerRequired || visibility === "shared" });
  const people = useMemo(() => (team.data ? commercials(team.data, locale) : []), [team.data, locale]);
  const person = forOthers ? (people.find((candidate) => candidate.membershipId === chosenPerson) ?? people[0] ?? null) : null;
  const forPerson = scope === "person" && person !== null;
  // Senza un Commerciale da scegliere il Super admin di Gioia365 non può creare la lista: perché, detto sotto la scelta.
  const personMissing = ownerRequired && person === null;
  const personReasonId = `${baseId}-person-reason`;
  const platformReasonId = `${baseId}-platform-reason`;
  const owner = forPerson ? person.membershipId : me;
  const colleagues = people.filter((candidate) => candidate.membershipId !== owner);

  // I numeri di oggi: i Lead di questi filtri e, per un Commerciale, i suoi; sempre con `who=all`.
  const filtersCount = useQuery(leadListQuery(tab, countQueryOf(definition, sort), view));
  const personDefinition = person ? forPersonDefinition(definition, person.membershipId) : definition;
  const personCount = useQuery({ ...leadListQuery(tab, countQueryOf(personDefinition, sort), view), enabled: person !== null });
  const leadVisibility = useQuery(leadVisibilityQuery(tab));

  // Si comincia dal nome, come nel mock-up (la finestra altrimenti dà il focus a «Chiudi»).
  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  function fail(field: Field, text: (tr: Translate) => string) {
    setProblem({ field, text });
    if (field === "name") nameRef.current?.focus();
    else if (field === "person") personRef.current?.focus();
    else if (field === "recipients") (shareRef.current?.querySelector<HTMLInputElement>("input") ?? submitRef.current)?.focus();
    else submitRef.current?.focus();
  }

  /** Il rifiuto del server, spiegato sul campo che lo riguarda. */
  function explain(error: unknown) {
    if (error instanceof ApiRequestError) {
      switch (error.code) {
        case "smart_list_name_taken": {
          // «Per lui» il nome è preso nel menu del Commerciale, non in quello di chi crea.
          const ownerName = forPerson ? person.displayName : null;
          fail("name", (tr) => (ownerName ? tr("smartLists.nameTakenFor", { name: ownerName }) : tr("smartLists.nameTaken")));
          return;
        }
        case "recipients_required":
          fail("recipients", (tr) => tr("smartLists.recipientsRequired"));
          return;
        case "owner_not_eligible":
          if (forPerson) {
            void team.refetch();
            fail("person", (tr) => messageForError(tr, error));
            return;
          }
          break;
        case "validation_failed":
          // Un collega non più attivo nel frattempo: si rilegge il team, e la scelta si rifà.
          if (invalidFields(error).includes("recipientIds")) {
            void team.refetch();
            fail("recipients", (tr) => messageForError(tr, error));
            return;
          }
          break;
      }
    }
    fail("form", (tr) => messageForError(tr, error));
  }

  async function submit() {
    if (create.isPending || personMissing) return;
    const trimmed = name.trim();
    if (!trimmed) {
      fail("name", (tr) => tr("smartLists.nameRequired"));
      return;
    }
    const recipientIds = colleagues.filter((candidate) => recipients.has(candidate.membershipId)).map((candidate) => candidate.membershipId);
    if (visibility === "shared" && recipientIds.length === 0) {
      fail("recipients", (tr) => tr("smartLists.recipientsRequired"));
      return;
    }
    setProblem(null);
    const body: CreateSmartListBody = {
      name: trimmed,
      visibility,
      recipientIds: visibility === "shared" ? recipientIds : [],
      definition: forPerson ? personDefinition : definition,
      sort: { field: sort.field, direction: sort.direction },
      ...(forPerson ? { ownerMembershipId: person.membershipId } : {}),
    };
    try {
      const list = await create.mutateAsync(body);
      onCreated(list.id);
    } catch (error) {
      explain(error);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submit();
  }

  function toggleRecipient(membershipId: string, checked: boolean) {
    setRecipients((current) => {
      const next = new Set(current);
      if (checked) next.add(membershipId);
      else next.delete(membershipId);
      return next;
    });
    if (problem?.field === "recipients") setProblem(null);
  }

  const errorOf = (field: Field) => (problem?.field === field ? problem.text(t) : undefined);
  const todayText = (total: number | undefined) => {
    if (total === undefined) return null;
    return total === 1 ? t("smartLists.todayCountOne") : t("smartLists.todayCount", { count: formatCount(locale, total) });
  };
  const filtersToday = todayText(filtersCount.data?.total);
  const personToday = todayText(personCount.data?.total);
  const mode = leadVisibility.data?.mode;
  // «misto» non è né l'una né l'altra regola: la nota non dice nulla di sbagliato.
  const scopeNote = mode === "own" ? t("smartLists.scopeNoteOwner") : mode === "all" ? t("smartLists.scopeNoteAll") : null;
  const recipientsError = errorOf("recipients");
  const formError = errorOf("form");

  return (
    <Dialog
      open
      title={variant === "saveAs" ? t("smartLists.saveAsTitle") : t("smartLists.dialogTitle")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("smartLists.cancel")}</Button>
          <Button
            ref={submitRef}
            variant="primary"
            type="submit"
            form={formId}
            disabled={create.isPending || personMissing}
            aria-busy={create.isPending ? "true" : undefined}
            aria-describedby={personMissing ? personReasonId : undefined}
          >
            {variant === "saveAs" ? t("smartLists.saveAsSubmit") : t("smartLists.createAndOpen")}
          </Button>
        </>
      }
    >
      <form id={formId} className="g-stack" onSubmit={handleSubmit} aria-busy={create.isPending} noValidate>
        <TextField
          id={`${baseId}-name`}
          ref={nameRef}
          label={variant === "saveAs" ? t("smartLists.saveAsName") : t("smartLists.name")}
          value={name}
          maxLength={NAME_MAX_LENGTH}
          autoComplete="off"
          error={errorOf("name")}
          onChange={(event) => setName(event.target.value)}
        />

        <fieldset className="g-new-list__fieldset">
          <legend>{t("smartLists.whoSees")}</legend>
          {SMART_LIST_VISIBILITIES.map((option) => {
            const id = `${baseId}-visibility-${option}`;
            return (
              <div key={option} className="g-check">
                <input
                  id={id}
                  className="g-check__input"
                  type="radio"
                  name={`${baseId}-visibility`}
                  checked={visibility === option}
                  aria-describedby={`${id}-hint`}
                  onChange={() => {
                    setVisibility(option);
                    if (problem?.field === "recipients") setProblem(null);
                  }}
                />
                <label className="g-check__label" htmlFor={id}>{t(`smartLists.visibility.${option}`)}</label>
                <p id={`${id}-hint`} className="g-check__hint">{t(`smartLists.visibilityHint.${option}`)}</p>
              </div>
            );
          })}
        </fieldset>

        {visibility === "shared" ? (
          <fieldset ref={shareRef} className="g-new-list__fieldset">
            <legend>{t("smartLists.shareWith")}</legend>
            {team.isError && !team.data ? (
              <>
                <p className="g-new-list__note">{messageForError(t, team.error)}</p>
                <Button onClick={() => void team.refetch()}>{t("common.retry")}</Button>
              </>
            ) : !team.data ? (
              <p className="g-new-list__note">{t("common.loading")}</p>
            ) : colleagues.length === 0 ? (
              <p className="g-new-list__note">{t("smartLists.noColleagues")}</p>
            ) : (
              colleagues.map((colleague) => (
                <Checkbox
                  key={colleague.membershipId}
                  id={`${baseId}-recipient-${colleague.membershipId}`}
                  label={colleague.displayName}
                  checked={recipients.has(colleague.membershipId)}
                  onChange={(checked) => toggleRecipient(colleague.membershipId, checked)}
                />
              ))
            )}
            {recipientsError ? (
              <p className="g-field__error" role="alert">
                {recipientsError}
              </p>
            ) : null}
          </fieldset>
        ) : null}

        <fieldset className="g-new-list__fieldset">
          <legend>{t("smartLists.whoseLeads")}</legend>
          {forOthers ? (
            <>
              <div className="g-check">
                <input
                  id={`${baseId}-scope-filters`}
                  className="g-check__input"
                  type="radio"
                  name={`${baseId}-scope`}
                  checked={!forPerson && !ownerRequired}
                  disabled={ownerRequired}
                  aria-describedby={ownerRequired ? platformReasonId : undefined}
                  onChange={() => setScope("filters")}
                />
                <label className="g-check__label" htmlFor={`${baseId}-scope-filters`}>
                  <span>
                    <strong>{t("smartLists.theseFilters")}</strong>
                    {filtersToday ? <span className="g-new-list__count">{filtersToday}</span> : null}
                  </span>
                </label>
                {ownerRequired ? (
                  <p id={platformReasonId} className="g-check__hint">
                    {t("smartLists.theseFiltersPlatform")}
                  </p>
                ) : null}
              </div>
              {person ? (
                <div className="g-check">
                  <input
                    id={`${baseId}-scope-person`}
                    className="g-check__input"
                    type="radio"
                    name={`${baseId}-scope`}
                    checked={forPerson}
                    onChange={() => setScope("person")}
                  />
                  <label className="g-check__label" htmlFor={`${baseId}-scope-person`}>{t("smartLists.forPerson")}</label>
                  <div className="g-new-list__person">
                    <label className="g-visually-hidden" htmlFor={`${baseId}-person`}>{t("smartLists.personSelect")}</label>
                    <select
                      ref={personRef}
                      id={`${baseId}-person`}
                      className="g-select"
                      value={person.membershipId}
                      aria-invalid={errorOf("person") ? "true" : undefined}
                      aria-describedby={errorOf("person") ? `${baseId}-person-error` : undefined}
                      onChange={(event) => {
                        setChosenPerson(event.target.value);
                        setScope("person");
                        if (problem?.field === "person") setProblem(null);
                      }}
                    >
                      {people.map((candidate) => (
                        <option key={candidate.membershipId} value={candidate.membershipId}>{candidate.displayName}</option>
                      ))}
                    </select>
                    {personToday ? <span className="g-new-list__count">{personToday}</span> : null}
                  </div>
                  {errorOf("person") ? (
                    <p id={`${baseId}-person-error`} className="g-field__error g-check__hint" role="alert">
                      {errorOf("person")}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : (
            <p>
              <strong>{t("smartLists.theseFilters")}</strong>
              {filtersToday ? <span className="g-new-list__count">{filtersToday}</span> : null}
            </p>
          )}
          <p className="g-new-list__note">{t("smartLists.noOwnerChange")}</p>
          {personMissing ? <PersonMissing id={personReasonId} team={team} /> : null}
          {scopeNote ? <p className="g-new-list__note">{scopeNote}</p> : null}
          {mineActive ? <p className="g-new-list__note">{t("smartLists.mineNotSaved")}</p> : null}
        </fieldset>

        <p className="g-new-list__note">{variant === "saveAs" ? t("smartLists.saveAsNote") : t("smartLists.saveNote")}</p>
        {formError ? (
          <p className="g-field__error" role="alert">
            {formError}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

type TeamState = { data?: unknown; isError: boolean; error: unknown; refetch: () => unknown };

/** Perché il Super admin di Gioia365 non può ancora creare la lista: il team si legge, non risponde (con «Riprova») o non ha User attivi. */
function PersonMissing({ id, team }: { id: string; team: TeamState }) {
  const t = useT();
  if (team.isError && team.data === undefined) {
    return (
      <>
        <Reason id={id} tone="warning" role="alert">{messageForError(t, team.error)}</Reason>
        <Button onClick={() => void team.refetch()}>{t("common.retry")}</Button>
      </>
    );
  }
  return <Reason id={id}>{team.data === undefined ? t("common.loading") : t("smartLists.noCommercials")}</Reason>;
}
